import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import type { FieldValues } from 'react-hook-form';
import { z } from 'zod';

import type { ApiError, StepUpAction, StepUpGrant } from '@/shared/http';
import type { PatternText } from '@/shared/patterns';

import { validateAction, type ActionDefinition } from './define-action';

/**
 * O que a operação sensível recebe depois do step-up: o `sessionId` do `POST /Auth/StartAuth` e o
 * código TOTP que a pessoa digitou. Cada endpoint põe os dois no query ou no corpo, como o legado.
 */
export interface StepUpProof {
  readonly sessionId: string;
  readonly code: string;
}

/**
 * O pedido do step-up novo (`X-UY3-StepUp`, CreditAPI): a ação do enum e o registro da rota protegida. `resourceId`
 * é o mesmo valor que vai no caminho (`ResourceRouteParam`); ausente na criação, que exige autorização sem registro.
 */
export interface StepUpGrantRequest {
  readonly action: StepUpAction;
  readonly resourceId?: string | null | undefined;
}

/**
 * A operação do step-up novo: `run` recebe a autorização de uso único e a passa como `{ stepUp: grant }` na chamada
 * protegida (`shared/http` põe o `X-UY3-StepUp`). Cada envio pede uma autorização nova.
 */
export interface StepUpGrantConfirm<R> {
  readonly grant: StepUpGrantRequest;
  readonly run: (grant: StepUpGrant) => Promise<R>;
}

/** O que o painel confirma: a operação com `sessionId` + `code` (StartAuth) ou com a autorização do step-up novo. */
export type StepUpConfirm<R> = ((proof: StepUpProof) => Promise<R>) | StepUpGrantConfirm<R>;

/** Monta a confirmação do step-up novo (`withStepUp(action, resourceId, call)` do legado, `stepUpContext.tsx`). */
export function grantConfirm<R>(grant: StepUpGrantRequest, run: (grant: StepUpGrant) => Promise<R>): StepUpGrantConfirm<R> {
  return { grant, run };
}

export function isGrantConfirm<R>(confirm: StepUpConfirm<R>): confirm is StepUpGrantConfirm<R> {
  return typeof confirm !== 'function';
}

/** Recusa da CreditAPI na chamada protegida: autorização ausente, vencida, já usada ou de outra ação ou registro. */
export const STEP_UP_REQUIRED = 'STEP_UP_REQUIRED';

/** Quem fechou o painel sem confirmar: erro `canceled`, que não vira toast (o legado mostrava "Operação cancelada"). */
export const STEP_UP_CANCELLED = 'STEP_UP_CANCELLED';

/** O código do erro do step-up, em camelCase ou PascalCase (`getStepUpErrorCode`, `stepUp.ts:110-113` do legado). */
export function stepUpErrorCode(error: ApiError): string {
  if (error.code !== 'UNKNOWN') return error.code;
  const pascal = (error.payload as { Code?: unknown }).Code;
  return typeof pascal === 'string' ? pascal : error.code;
}

export function isStepUpRequired(error: ApiError): boolean {
  return error.status === 403 && stepUpErrorCode(error) === STEP_UP_REQUIRED;
}

const GRANT_MESSAGES = {
  STEP_UP_REQUIRED: msg`Confirme a operação com sua senha e o código MFA.`,
  STEP_UP_HUMAN_ONLY: msg`Esta operação exige um usuário humano.`,
  STEP_UP_INVALID_CREDENTIALS: msg`Senha ou código inválido.`,
  STEP_UP_MFA_NOT_CONFIGURED: msg`Configure o MFA (aplicativo autenticador) no seu perfil antes de realizar esta operação.`,
  STEP_UP_CHALLENGE_INVALID: msg`A confirmação expirou. Informe a senha novamente.`,
  STEP_UP_RATE_LIMITED: msg`Muitas tentativas. Aguarde alguns minutos.`,
  STEP_UP_UNAVAILABLE: msg`Confirmação indisponível no momento. Tente novamente em instantes.`,
} as const;

/**
 * Texto de um erro do step-up novo (`mapStepUpError`, `services/stepUp/stepUp.ts` do legado): as descrições do
 * `DomainErrorEnum` da CreditAPI, que é o `message` que o legado mostra. No Start e no Verify (`challenge`),
 * `INVALID_FORM` vira "Senha ou código inválido." e falha sem resposta, o texto de indisponível do legado.
 * `undefined`: não é do step-up (vale o `feedback()` comum).
 */
export function stepUpErrorMessage(error: ApiError, i18n: I18n, options: { readonly challenge?: boolean } = {}): string | undefined {
  const code = stepUpErrorCode(error);
  if (code in GRANT_MESSAGES) return i18n._(GRANT_MESSAGES[code as keyof typeof GRANT_MESSAGES]);
  if (!options.challenge) return undefined;
  if (code === 'INVALID_FORM') return i18n._(GRANT_MESSAGES.STEP_UP_INVALID_CREDENTIALS);
  if (error.kind !== 'response') return i18n._(msg`Não foi possível confirmar a operação agora. Tente novamente em alguns minutos.`);
  return undefined;
}

/** Senha e código do formulário do step-up (`ConfirmMfaFormSchema.ts` do legado). */
export interface StepUpValues {
  password: string;
  code: string;
}

/**
 * Textos do painel, para seguir cada consumidor do legado (título e confirmar próprios). O voltar não se configura:
 * é "Voltar" com etapa anterior e "Cancelar" na primeira (W11, U53).
 */
export interface StepUpTexts {
  /** Título do painel. Padrão: "Confirme a operação" (`ConfirmMfaFormDrawer.tsx:41`). */
  readonly title?: PatternText;
  /** Botão de confirmar. Padrão: "Confirmar operação" (`ConfirmMfaFormDrawer.tsx:47`). */
  readonly submitLabel?: PatternText;
}

/** Uma linha da revisão de uma ação com step-up (o `ReviewRow` do Sheet, sem o nó React no rótulo). */
export interface StepUpReviewRow {
  readonly id: string;
  readonly label: string;
  readonly value: string;
}

/**
 * A etapa "Revisão" da ação com formulário (W11): as linhas do que vai no fio, abaixo da prévia; `'fields'` quando os
 * próprios `Fields` mostram o resumo (o "Resumo da operação" do boleto) e o trilho é avisado por `useActionStep`.
 */
export type StepUpReview<C, V> = ((values: V, ctx: C, i18n: I18n) => readonly StepUpReviewRow[]) | 'fields';

export const STEP_UP_DEFAULTS: StepUpValues = { password: '', code: '' };

/** Código do backend para código TOTP errado ou vencido; a mensagem é `details.reason` (legado). */
export const INVALID_MFA = 'INVALID_MFA';

/** Validação do legado (`ConfirmMfaFormSchema.ts:3-11`), com as mesmas mensagens. */
export function stepUpSchema(i18n: I18n) {
  const digits = i18n._(msg`Deve possuir 6 dígitos`);
  return z.object({
    password: z.string().min(1, i18n._(msg`Senha: Precisa ser preenchido.`)),
    code: z.string().min(1, i18n._(msg`Código MFA: precisa ser preenchido.`)).length(6, digits),
  });
}

/**
 * A prova é um objeto que não se serializa por acidente: `JSON.stringify` e template string mostram
 * `[step-up]`. Espalhar (`{ ...proof }`) e ler os campos continuam funcionando.
 */
export function stepUpProof(sessionId: string, code: string): StepUpProof {
  const proof = { sessionId, code };
  Object.defineProperties(proof, {
    toJSON: { value: () => '[step-up]', enumerable: false },
    toString: { value: () => '[step-up]', enumerable: false },
  });
  return Object.freeze(proof);
}

/** Query dos endpoints que levam a prova na URL (`?sessionId=…&code=…`, `bankAccount.ts:558`, `:628`, `:780`). */
export function stepUpParams(proof: StepUpProof): { sessionId: string; code: string } {
  return { sessionId: proof.sessionId, code: proof.code };
}

/** Código errado ou vencido: vai no campo do código, com a mensagem do backend (`responseErrorContext.tsx:269`). */
export function isInvalidMfa(error: ApiError): boolean {
  return error.code === INVALID_MFA;
}

const STEP_UP = Symbol('stepUp');

export interface StepUpSubmit<C, V extends FieldValues, R> {
  readonly submit: (values: V, ctx: C, proof: StepUpProof) => Promise<R>;
  readonly texts: StepUpTexts;
  readonly review?: StepUpReview<C, V>;
  /** Step-up novo (`withStepUpGrant`): a confirmação com a autorização de uso único, no lugar da prova. */
  readonly grant?: (values: V, ctx: C) => StepUpGrantConfirm<R>;
}

/**
 * A ação com step-up: igual a `ActionDefinition`, mas o `submit` recebe a prova. Sem `undo`: dinheiro
 * e operação bancária não têm estorno confirmado (X14).
 */
export interface StepUpActionDefinition<C, V extends FieldValues = Record<string, never>, R = unknown>
  extends Omit<ActionDefinition<C, V, R>, 'submit' | 'undo'> {
  readonly submit: (values: V, ctx: C, proof: StepUpProof) => Promise<R>;
  readonly stepUp?: StepUpTexts;
  /** A Revisão entre os dados e o código; ausente, a revisão mostra só a prévia (`summary`). */
  readonly review?: StepUpReview<C, V>;
}

/** A ação do step-up novo (`X-UY3-StepUp`): `grant` diz a ação e o registro; o `submit` recebe a autorização. */
export interface StepUpGrantActionDefinition<C, V extends FieldValues = Record<string, never>, R = unknown>
  extends Omit<StepUpActionDefinition<C, V, R>, 'submit'> {
  readonly grant: (values: V, ctx: C) => StepUpGrantRequest;
  readonly submit: (values: V, ctx: C, grant: StepUpGrant) => Promise<R>;
}

type WithStepUp<C, V extends FieldValues, R> = ActionDefinition<C, V, R> & { readonly [STEP_UP]: StepUpSubmit<C, V, R> };

const refuse = (id: string) => () =>
  Promise.reject(new Error(`ação ${id}: exige step-up (senha e código MFA) e só roda pelo diálogo`));

function wrap<C, V extends FieldValues, R>(
  rest: Omit<ActionDefinition<C, V, R>, 'submit'>,
  stepUp: StepUpSubmit<C, V, R>,
): ActionDefinition<C, V, R> {
  const wrapped: WithStepUp<C, V, R> = { ...rest, submit: refuse(rest.id), [STEP_UP]: stepUp };
  if (import.meta.env.DEV) {
    const issues = validateAction(wrapped);
    if (issues.length > 0) throw new Error(issues.join('; '));
  }
  return wrapped;
}

/**
 * Embrulha a ação para o `ActionDialog` pedir senha + código antes do envio (F2a). O `submit` comum
 * da definição devolvida recusa: o efeito só roda pelo diálogo, com o que a pessoa digitou (U24).
 */
export function withStepUp<C, V extends FieldValues = Record<string, never>, R = unknown>(
  action: StepUpActionDefinition<C, V, R>,
): ActionDefinition<C, V, R> {
  const { submit, stepUp, review, ...rest } = action;
  return wrap(rest, { submit, texts: stepUp ?? {}, ...(review ? { review } : {}) });
}

/**
 * O mesmo Sheet com o step-up novo da CreditAPI (`StepUp/Start` + `StepUp/Verify` → `X-UY3-StepUp`, Segurança):
 * o `submit` recebe a autorização de uso único e a passa como `{ stepUp: grant }` na chamada protegida.
 */
export function withStepUpGrant<C, V extends FieldValues = Record<string, never>, R = unknown>(
  action: StepUpGrantActionDefinition<C, V, R>,
): ActionDefinition<C, V, R> {
  const { submit, stepUp, review, grant, ...rest } = action;
  return wrap(rest, {
    submit: refuse(rest.id),
    grant: (values, ctx) => grantConfirm(grant(values, ctx), (authorization) => submit(values, ctx, authorization)),
    texts: stepUp ?? {},
    ...(review ? { review } : {}),
  });
}

/** O envio com prova da ação embrulhada por `withStepUp`; `undefined` para ação comum. */
export function stepUpOf<C, V extends FieldValues, R>(action: ActionDefinition<C, V, R>): StepUpSubmit<C, V, R> | undefined {
  return (action as Partial<WithStepUp<C, V, R>>)[STEP_UP];
}

/** A ação pede step-up? */
export function isStepUpAction(action: object): boolean {
  return STEP_UP in action;
}
